/**
 * DefaultUserSettingsPanel — what a person's app looks like before they have
 * touched anything: theme, interface language, and whether the AI keeps a
 * personal memory profile.
 *
 * The hard part here is not the three controls, it is being honest about what
 * each button does. Three different things could be meant by "set the default
 * to Hebrew", and conflating them is how an admin accidentally rewrites
 * hundreds of people's settings:
 *
 *   1. Save            — new users get Hebrew. Nobody's existing choice moves.
 *   2. Apply to users  — everyone here gets Hebrew NOW, choice or not.
 *   3. Apply to sub-tenants — every sub-tenant's own default becomes Hebrew.
 *
 * So they are three separate, separately-confirmed actions, and the panel
 * states the blast radius of each in people and tenants before it runs.
 *
 * Each control has a third state, "Inherit", which is the real default: the
 * value then resolves up the tenant chain, and the panel names the ancestor it
 * came from. Without that state a tenant could only ever pin a value, and an
 * inherited value would be indistinguishable from a chosen one.
 */

import { useCallback, useEffect, useState } from 'react';
import { Button, Modal, Segmented, Switch, Tag, Tooltip, message } from 'antd';
import { useLanguage } from '@/hooks/useLanguage';
import defaultUserSettingsService, {
  type DefaultSettingKey,
  type DefaultUserSettingsPayload,
  type OwnUserDefaults,
} from '@modules/super_admin/services/defaultUserSettingsService';
import AccessRow from './AccessRow';
import '@modules/super_admin/console/console-shell.css';

const INHERIT = '__inherit__';

interface Props {
  tenantId: string;
  tenantName: string;
  /** Lets the editor rail mark this group as configured. */
  onConfiguredChange?: (configured: boolean) => void;
}

export default function DefaultUserSettingsPanel({ tenantId, tenantName, onConfiguredChange }: Props) {
  const { t } = useLanguage();
  const [data, setData] = useState<DefaultUserSettingsPayload | null>(null);
  const [draft, setDraft] = useState<OwnUserDefaults>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await defaultUserSettingsService.get(tenantId);
      setData(payload);
      setDraft(payload.default_user_settings.own);
      onConfiguredChange?.(Object.keys(payload.default_user_settings.own).length > 0);
    } catch {
      message.error(t('superAdmin.defaults.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [tenantId, t, onConfiguredChange]);

  useEffect(() => { void load(); }, [load]);

  const own = data?.default_user_settings.own ?? {};
  const dirty = (['theme', 'language', 'ai_personalization'] as DefaultSettingKey[])
    .some(k => draft[k] !== own[k]);

  /** The value a control should show: the tenant's own choice, or — when it is
   *  inheriting — the resolved value, so the control never looks empty. */
  const shown = <K extends DefaultSettingKey>(key: K) =>
    draft[key] === undefined ? INHERIT : String(draft[key]);

  const set = (key: DefaultSettingKey, raw: string) => {
    setDraft(prev => {
      const next = { ...prev };
      if (raw === INHERIT) {
        delete next[key];
      } else if (key === 'ai_personalization') {
        next.ai_personalization = raw === 'true';
      } else if (key === 'theme') {
        next.theme = raw as 'light' | 'dark';
      } else {
        next.language = raw as 'en' | 'he';
      }
      return next;
    });
  };

  /** "Inherited from Eng Dept" / "Platform default" — the reason a control
   *  showing Inherit still displays a concrete value. */
  const sourceNote = (key: DefaultSettingKey) => {
    if (!data || draft[key] !== undefined) return null;
    const src = data.default_user_settings.sources[key];
    const value = String(data.default_user_settings.resolved[key]);
    const label = t(`superAdmin.defaults.value.${key}.${value}`);
    if (src?.kind === 'inherited') {
      return (
        <span className="sa-defaults-source">
          {t('superAdmin.defaults.inheritedFrom')
            .replace('{{value}}', label)
            .replace('{{tenant}}', src.tenant_name)}
        </span>
      );
    }
    return (
      <span className="sa-defaults-source">
        {t('superAdmin.defaults.platformDefault').replace('{{value}}', label)}
      </span>
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      // Send every key explicitly, with null for the ones set to Inherit. An
      // omitted key would be indistinguishable from "leave it alone", and the
      // endpoint replaces the whole blob.
      const payload = await defaultUserSettingsService.save(tenantId, {
        theme: draft.theme ?? null,
        language: draft.language ?? null,
        ai_personalization: draft.ai_personalization ?? null,
      });
      setData(payload);
      setDraft(payload.default_user_settings.own);
      onConfiguredChange?.(Object.keys(payload.default_user_settings.own).length > 0);
      message.success(t('superAdmin.defaults.saved'));
    } catch {
      message.error(t('superAdmin.defaults.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  /** Overwrite people's existing choices. Confirmed with a real count. */
  const applyToUsers = (includeDescendants: boolean) => {
    const count = includeDescendants
      ? (data?.user_counts.subtree ?? 0)
      : (data?.user_counts.tenant ?? 0);
    Modal.confirm({
      title: t('superAdmin.defaults.applyUsersTitle'),
      content: t('superAdmin.defaults.applyUsersBody')
        .replace('{{count}}', String(count))
        .replace('{{scope}}', includeDescendants
          ? t('superAdmin.defaults.scopeSubtree')
          : tenantName),
      okText: t('superAdmin.defaults.applyUsersOk'),
      okButtonProps: { danger: true },
      onOk: async () => {
        setBusy('users');
        try {
          const res = await defaultUserSettingsService.applyToUsers(tenantId, { includeDescendants });
          message.success(
            t('superAdmin.defaults.appliedUsers').replace('{{count}}', String(res.users_written ?? 0)),
          );
        } catch {
          message.error(t('superAdmin.defaults.applyFailed'));
        } finally {
          setBusy(null);
        }
      },
    });
  };

  /** Overwrite sub-tenants' own defaults. */
  const applyToDescendants = () => {
    Modal.confirm({
      title: t('superAdmin.defaults.applyTenantsTitle'),
      content: t('superAdmin.defaults.applyTenantsBody'),
      okText: t('superAdmin.defaults.applyTenantsOk'),
      okButtonProps: { danger: true },
      onOk: async () => {
        setBusy('tenants');
        try {
          const res = await defaultUserSettingsService.applyToDescendants(tenantId);
          message.success(
            t('superAdmin.defaults.appliedTenants').replace('{{count}}', String(res.count)),
          );
          await load();
        } catch {
          message.error(t('superAdmin.defaults.applyFailed'));
        } finally {
          setBusy(null);
        }
      },
    });
  };

  const toggleEnforcement = async (enforced: boolean) => {
    setBusy('enforce');
    try {
      const res = await defaultUserSettingsService.setEnforcement(tenantId, enforced);
      message.success(
        enforced
          ? t('superAdmin.defaults.enforcedOn').replace('{{count}}', String(res.descendants_updated))
          : t('superAdmin.defaults.enforcedOff'),
      );
      await load();
    } catch {
      message.error(t('superAdmin.defaults.applyFailed'));
    } finally {
      setBusy(null);
    }
  };

  if (loading && !data) {
    return <p className="sa-access-note">{t('common.loading')}</p>;
  }

  const hasChildren = !!data?.cascade.has_children;

  return (
    <div className="sa-defaults" data-testid="wl-default-user-settings">
      <section className="sa-panel">
        <header className="sa-panel-head">
          <h3>{t('superAdmin.defaults.title')}</h3>
          <p>{t('superAdmin.defaults.subtitle')}</p>
        </header>

        <div className="sa-panel-body">
          <AccessRow
            testId="defaults-row-theme"
            label={t('superAdmin.defaults.themeLabel')}
            hint={t('superAdmin.defaults.themeHint')}
            footnote={sourceNote('theme')}
          >
            <Segmented
              value={shown('theme')}
              onChange={v => set('theme', String(v))}
              data-testid="defaults-theme"
              options={[
                { label: t('superAdmin.defaults.inherit'), value: INHERIT },
                { label: t('superAdmin.defaults.value.theme.light'), value: 'light' },
                { label: t('superAdmin.defaults.value.theme.dark'), value: 'dark' },
              ]}
            />
          </AccessRow>

          <AccessRow
            testId="defaults-row-language"
            label={t('superAdmin.defaults.languageLabel')}
            hint={t('superAdmin.defaults.languageHint')}
            footnote={sourceNote('language')}
          >
            <Segmented
              value={shown('language')}
              onChange={v => set('language', String(v))}
              data-testid="defaults-language"
              options={[
                { label: t('superAdmin.defaults.inherit'), value: INHERIT },
                { label: t('superAdmin.defaults.value.language.en'), value: 'en' },
                { label: t('superAdmin.defaults.value.language.he'), value: 'he' },
              ]}
            />
          </AccessRow>

          <AccessRow
            testId="defaults-row-personalization"
            label={t('superAdmin.defaults.personalizationLabel')}
            hint={t('superAdmin.defaults.personalizationHint')}
            footnote={sourceNote('ai_personalization')}
          >
            <Segmented
              value={shown('ai_personalization')}
              onChange={v => set('ai_personalization', String(v))}
              data-testid="defaults-personalization"
              options={[
                { label: t('superAdmin.defaults.inherit'), value: INHERIT },
                { label: t('superAdmin.defaults.value.ai_personalization.true'), value: 'true' },
                { label: t('superAdmin.defaults.value.ai_personalization.false'), value: 'false' },
              ]}
            />
          </AccessRow>
        </div>

        <footer className="sa-defaults-foot">
          <span className="sa-access-hint">
            {t('superAdmin.defaults.saveScope')
              .replace('{{count}}', String(data?.user_counts.tenant ?? 0))}
          </span>
          <Button
            type="primary"
            disabled={!dirty}
            loading={saving}
            onClick={save}
            data-testid="defaults-save"
          >
            {t('common.save')}
          </Button>
        </footer>
      </section>

      {/* ── Reaching people and tenants who already have settings ────── */}
      <section className="sa-panel">
        <header className="sa-panel-head">
          <h3>{t('superAdmin.defaults.rolloutTitle')}</h3>
          <p>{t('superAdmin.defaults.rolloutSubtitle')}</p>
        </header>
        <div className="sa-panel-body">
          <AccessRow
            testId="defaults-row-apply-users"
            label={t('superAdmin.defaults.applyUsersLabel')}
            hint={t('superAdmin.defaults.applyUsersHint')
              .replace('{{count}}', String(data?.user_counts.tenant ?? 0))}
          >
            <div className="sa-defaults-actions">
              <Button
                danger
                loading={busy === 'users'}
                onClick={() => applyToUsers(false)}
                data-testid="defaults-apply-users"
              >
                {t('superAdmin.defaults.applyUsersBtn')
                  .replace('{{count}}', String(data?.user_counts.tenant ?? 0))}
              </Button>
              {hasChildren && (
                <Button
                  danger
                  loading={busy === 'users'}
                  onClick={() => applyToUsers(true)}
                  data-testid="defaults-apply-users-subtree"
                >
                  {t('superAdmin.defaults.applyUsersSubtreeBtn')
                    .replace('{{count}}', String(data?.user_counts.subtree ?? 0))}
                </Button>
              )}
            </div>
          </AccessRow>

          {/* Everything below only exists if there IS a subtree. Rendering
              disabled cascade controls on a leaf tenant would be noise. */}
          {hasChildren ? (
            <>
              <AccessRow
                compact
                testId="defaults-row-enforce"
                label={t('superAdmin.defaults.enforceLabel')}
                hint={t('superAdmin.defaults.enforceHint')}
                footnote={
                  data?.cascade.differs_from_parent
                    ? t('superAdmin.defaults.driftNote')
                        .replace('{{parent}}', data.cascade.parent?.tenant_name ?? '')
                    : undefined
                }
              >
                <Switch
                  checked={!!data?.cascade.enforced}
                  loading={busy === 'enforce'}
                  onChange={toggleEnforcement}
                  data-testid="defaults-enforce"
                />
              </AccessRow>

              <AccessRow
                testId="defaults-row-apply-tenants"
                label={t('superAdmin.defaults.applyTenantsLabel')}
                hint={t('superAdmin.defaults.applyTenantsHint')}
              >
                <Tooltip title={data?.cascade.enforced ? t('superAdmin.defaults.alreadyEnforced') : ''}>
                  <span>
                    <Button
                      danger
                      loading={busy === 'tenants'}
                      onClick={applyToDescendants}
                      data-testid="defaults-apply-tenants"
                    >
                      {t('superAdmin.defaults.applyTenantsBtn')}
                    </Button>
                  </span>
                </Tooltip>
              </AccessRow>
            </>
          ) : (
            <p className="sa-access-note">{t('superAdmin.defaults.noChildren')}</p>
          )}

          {data?.cascade.parent_enforced && (
            <p className="sa-access-note" data-testid="defaults-parent-enforced">
              <Tag color="orange">{t('superAdmin.defaults.parentEnforcedTag')}</Tag>
              {t('superAdmin.defaults.parentEnforcedNote')
                .replace('{{parent}}', data.cascade.parent?.tenant_name ?? '')}
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
